import { useState } from "react";
import { BadgeCheck, Mail, AlertCircle, Loader2, PartyPopper, Linkedin } from "lucide-react";
import { toast } from "sonner";
import api from "../../../lib/api";
import { CodeEntry, WorkStatusSelect, errText } from "../../auth/EmailCode";
import ShareWinModal from "../ShareWinModal";
import ProfilePhoto from "./ProfilePhoto";

function EmailVerify({ onVerified }) {
  const [sentTo, setSentTo] = useState("");
  const [sending, setSending] = useState(false);
  const send = () => api.post("/auth/code/verify-email/send");
  const start = async () => {
    setSending(true);
    try {
      const { data } = await send();
      if (data.already) return onVerified();
      setSentTo(data.sent_to);
    } catch (e) {
      toast.error(errText(e));
    } finally {
      setSending(false);
    }
  };
  return (
    <div className="mt-5 rounded-xl border border-warning/30 bg-warning-soft p-4" data-testid="profile-verify-email">
      <div className="flex items-start gap-2.5">
        <AlertCircle className="w-5 h-5 text-warning mt-0.5 shrink-0" aria-hidden="true" />
        <div className="min-w-0 flex-1">
          <div className="font-semibold text-ink">Confirm your email address</div>
          <p className="text-sm text-body mt-0.5">We'll email you a one-time code so we know this address is really yours.</p>
          <div className="mt-3 max-w-sm">
            {sentTo ? (
              <CodeEntry sentTo={sentTo} cta="Verify my email" testid="profile-verify-code" onResend={send}
                onVerify={async (code) => { await api.post("/auth/code/verify-email/confirm", { code }); toast.success("Your email is verified."); onVerified(); }} />
            ) : (
              <button data-testid="profile-verify-send" onClick={start} disabled={sending} className="gi-btn gi-btn-primary gi-btn-sm">
                {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" aria-hidden="true" />} Email me a code
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function ProfileHeader({ p, score, onChange, onVerified }) {
  const [share, setShare] = useState(false);
  return (
    <div className="gi-card p-5 sm:p-6" data-testid="profile-header">
      <div className="flex flex-col md:flex-row md:items-center gap-5">
        <div className="flex items-center gap-4 min-w-0 flex-1">
          <ProfilePhoto p={p} onChange={onChange} />
          <div className="min-w-0">
            <div className="font-display text-xl font-bold text-ink truncate" data-testid="profile-display-name">{p.name || "Add your name below"}</div>
            <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1.5 text-sm text-body">
              <span className="inline-flex items-center gap-1.5 min-w-0 max-w-full">
                <Mail className="w-4 h-4 text-faint shrink-0" aria-hidden="true" />
                <span className="truncate" data-testid="profile-email">{p.email || "No email on file"}</span>
              </span>
              {p.email && (p.email_verified ? (
                <span data-testid="profile-email-verified" className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-success-soft text-success text-xs font-semibold"><BadgeCheck className="w-3.5 h-3.5" aria-hidden="true" /> Verified</span>
              ) : (
                <span data-testid="profile-email-unverified" className="inline-flex items-center px-2 py-0.5 rounded-full bg-warning-soft text-warning text-xs font-semibold">Not verified</span>
              ))}
            </div>
          </div>
        </div>
        <div className="md:w-72">
          <label htmlFor="profile-work-status" className="gi-label">Work status</label>
          <WorkStatusSelect id="profile-work-status" value={p.work_status} onChange={(v) => onChange("work_status", v)} testid="profile-work-status" />
        </div>
      </div>
      {p.email && !p.email_verified && <EmailVerify onVerified={onVerified} />}
      <div className="mt-5" data-testid="profile-completeness">
        <div className="flex items-center justify-between text-sm"><span className="font-medium text-ink">Profile strength</span><span className="font-semibold text-brand-text" data-testid="profile-completeness-value">{score}%</span></div>
        <div className="mt-2 h-2 rounded-full bg-subtle overflow-hidden"><div className="h-full rounded-full bg-brand transition-[width] duration-500" style={{ width: `${score}%` }} /></div>
        {score === 100 ? (
          <div className="mt-4 flex flex-wrap items-center gap-3 rounded-xl bg-brand-soft/60 border border-brand/20 p-3" data-testid="profile-share-ready">
            <PartyPopper className="w-5 h-5 text-brand shrink-0" aria-hidden="true" />
            <span className="text-sm font-medium text-ink flex-1 min-w-[12rem]">Your profile is 100% ready. Let your network know.</span>
            <button data-testid="profile-share-button" onClick={() => setShare(true)} className="gi-btn gi-btn-primary gi-btn-sm"><Linkedin className="w-4 h-4" aria-hidden="true" /> Share on LinkedIn</button>
          </div>
        ) : (
          <p className="mt-2 text-xs text-faint" data-testid="profile-share-locked">Reach 100% to unlock a card you can share on LinkedIn.</p>
        )}
      </div>
      <ShareWinModal open={share} onOpenChange={setShare} kind="profile" />
    </div>
  );
}
